import { v4 as uuidv4 } from "uuid";

import { ProjectRow, SelectData } from "../../../../../models";
import { readXlsxWorkbook } from "../../../../../utils";

/*
  The fallback lists, used only for a row whose hidden derived cell is empty. Project types are the
  whole flat list rather than the per-standard slice: whether a type belongs to the standard beside
  it is BR-02887's call, made by the api, so resolving against the slice here would drop the uuid
  and hide the pairing the api needs to name both halves in its message.

  Countries must be the same narrowed list the form offers (key.includes("GB-")), which is the list
  the template's own dropdown is built from - resolving against a wider one would accept a country
  the sheet never offered.
*/
export interface WorkbookLookups {
  standardOptions: SelectData;
  projectTypeOptions: SelectData;
  verifierOptions: SelectData;
  countryOptions: SelectData;
}

/*
  The template's column names, which are fixed - an uploaded file carries these same headings. The
  server generates the sheet from the same contract (GenerateProjectImportTemplateService), matched
  by name and never by position, so adding a column at the end is safe and renaming one is not.

  Compared in normalised form (lowercase, punctuation reduced to single spaces) so that casing or a
  stray double space in a hand-edited file is not treated as a different column.
*/
const COLUMN_HEADERS = {
  displayName: "project name",
  projectTypeName: "project type",
  standardName: "standard",
  verifierName: "verifier vvb",
  addressCountryName: "address country",
  addressRegion: "address region",
  addressPostcode: "address postcode",
  latitude: "latitude",
  longitude: "longitude",
  // Superseded by the two columns above, which the template now carries instead. Kept because a
  // file built from an older one holds both halves in this single "56.3722, -3.9855" cell.
  locationCoordinates: "location coordinates",
  gridReference: "grid reference",
  areaNetHa: "area net ha",
  landholdingName: "landholding name",
  // The kind of reference, picked from a dropdown, and the reference itself. Neither is resolved
  // here: the kinds are a fixed list whose key and value are the same string, so the text picked is
  // what createGroup wants as externalParty.
  landholdingReference: "landholding reference",
  landholdingReferenceId: "landholding reference id",
  /*
    The hidden half of the contract: an INDEX/MATCH against the template's Lookups sheet, carrying
    what the dropdown beside it resolves to - a uuid for three of them, an ISO code for the country.

    "Address country code" sits here rather than among the visible columns, where earlier templates
    had it: the country is now picked by name from a dropdown and the code derived from it. Matching
    on header name is what makes that move invisible to this parser.
  */
  projectTypeUuid: "project type uuid",
  standardUuid: "standard uuid",
  verifierUuid: "verifier vvb uuid",
  addressCountryCode: "address country code",
} as const;

type ColumnKey = keyof typeof COLUMN_HEADERS;

// The derived columns are deliberately not required. The server's own contract treats a deleted one
// as a supported path that falls back to the name, so their absence is not an out-of-date template -
// only the columns a person actually fills in decide whether this is the table.
const REQUIRED_COLUMNS: ColumnKey[] = ["displayName", "projectTypeName", "standardName"];

// How far into a sheet to look for the heading row. Generous enough for a template with a title,
// a logo or an instructions block above the table, without scanning a whole workbook of data.
const HEADER_SEARCH_ROWS = 10;

export class MissingColumnsError extends Error {
  constructor(public readonly columns: string[]) {
    super(`Missing columns: ${columns.join(", ")}`);
    this.name = "MissingColumnsError";
  }
}

const normalise = (value: unknown): string => String(value ?? "").trim();

// Punctuation out, whitespace collapsed - the form a header is compared in.
const normaliseHeader = (value: unknown): string =>
  normalise(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

// Matches the option's display name or its key, so a sheet can carry either "Scotland" or "GB-SCT".
const findOptionKey = (options: SelectData, name: string): string | null => {
  const wanted = name.toLowerCase();
  const match = options.find(
    (option) => String(option.value ?? "").toLowerCase() === wanted || String(option.key).toLowerCase() === wanted
  );
  return match ? String(match.key) : null;
};

// Header cells are matched to fields rather than used as object keys - a workbook cannot inject a
// field name we do not already know about.
const mapHeaderIndexes = (header: unknown[]): Partial<Record<ColumnKey, number>> => {
  const indexes: Partial<Record<ColumnKey, number>> = {};

  (Object.keys(COLUMN_HEADERS) as ColumnKey[]).forEach((column) => {
    const index = header.findIndex((cell) => normaliseHeader(cell) === COLUMN_HEADERS[column]);
    if (index >= 0) indexes[column] = index;
  });

  return indexes;
};

const toNumber = (value: string): number | null => {
  if (!value) return null;
  const parsed = Number(value.replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
};

interface LocatedTable {
  body: unknown[][];
  headerRowIndex: number;
  indexes: Partial<Record<ColumnKey, number>>;
}

// The heading row is hunted for rather than assumed to be row 1 of sheet 1: templates pick up title
// rows and instructions tabs, and users paste the table into workbooks of their own.
const locateTable = (grids: unknown[][][]): LocatedTable => {
  let closest: ColumnKey[] | null = null;

  for (let sheet = 0; sheet < grids.length; sheet += 1) {
    const grid = grids[sheet];
    const limit = Math.min(grid.length, HEADER_SEARCH_ROWS);

    for (let rowIndex = 0; rowIndex < limit; rowIndex += 1) {
      const indexes = mapHeaderIndexes(grid[rowIndex] ?? []);
      const missing = REQUIRED_COLUMNS.filter((column) => indexes[column] === undefined);

      if (missing.length === 0) {
        return { body: grid.slice(rowIndex + 1), headerRowIndex: rowIndex, indexes };
      }
      // Remember the nearest miss so the error names the columns the user is most likely missing,
      // rather than all three every time.
      if (closest === null || missing.length < closest.length) closest = missing;
    }
  }

  throw new MissingColumnsError((closest ?? REQUIRED_COLUMNS).map((column) => COLUMN_HEADERS[column]));
};

const LANDHOLDING_REFERENCE_COLUMNS: ColumnKey[] = ["landholdingReference", "landholdingReferenceId"];

/*
  The landholding columns are optional - a file of standalone projects needs none of them - but a
  file that does fill them in needs both reference columns. A template from before the reference ID
  column was added carries only "Landholding reference", and read as it stands every landholding in
  it would go out with no reference for the api to refuse one by one; naming the missing column here
  points the user at the current template instead.
*/
const assertLandholdingColumns = (rows: ProjectRow[], indexes: Partial<Record<ColumnKey, number>>): void => {
  const hasLandholdingData = rows.some(
    (row) => row.landholdingName || row.landholdingReference || row.landholdingReferenceId
  );
  if (!hasLandholdingData) return;

  const missing = LANDHOLDING_REFERENCE_COLUMNS.filter((column) => indexes[column] === undefined);
  if (missing.length > 0) throw new MissingColumnsError(missing.map((column) => COLUMN_HEADERS[column]));
};

/*
  Reads the sheet into rows, taking the uuid the api wants from the template's hidden uuid column
  and falling back to resolving the name beside it. It reports nothing about the rows themselves -
  whether a name is long enough, a standard one this organisation can create under, or a type the
  standard carries, is the api's answer to give on the save, so a uuid that could not be found
  either way is carried as null with the text the file used kept beside it for the returned error
  to quote.

  Throws MissingColumnsError when no heading row can be found, which is not a rule about the data:
  without it there is nothing to map cells to and the file cannot be read at all.
*/
export const parseProjectWorkbook = async (file: File, lookups: WorkbookLookups): Promise<ProjectRow[]> => {
  const sheets = await readXlsxWorkbook(await file.arrayBuffer());
  const { body, indexes } = locateTable(sheets.map((sheet) => sheet.rows));

  const rows: ProjectRow[] = [];

  body.forEach((cells, bodyIndex) => {
    const cell = (column: ColumnKey): string => {
      const position = indexes[column];
      return position === undefined ? "" : normalise(cells[position]);
    };

    // Only a row with nothing at all in it is trailing spreadsheet padding. Judged across every
    // column the template has: a row carrying just an address or an area is a project someone left
    // the identity cells off, and dropping it would lose it before the api ever saw it.
    if ((Object.keys(COLUMN_HEADERS) as ColumnKey[]).every((column) => !cell(column))) return;

    const standardName = cell("standardName");
    const projectTypeName = cell("projectTypeName");
    const verifierName = cell("verifierName");

    /*
      The hidden derived column is the better answer, never the only one. It holds an INDEX/MATCH
      formula, and a formula is only as good as its last recalculation: a template downloaded and
      filled in by something that does not recalculate, rows pasted past the formula range, a
      paste-as-values or a deleted column all leave it empty with the name still there to resolve.
    */
    const resolveDerived = (derivedColumn: ColumnKey, name: string, options: SelectData): string | null =>
      cell(derivedColumn) || (name ? findOptionKey(options, name) : null);

    // Unlike the uuid columns, the country's derived cell is resolved too: an older template had
    // "Address country code" as a visible column, and people filled it with names ("England"). The
    // api only accepts a code. findOptionKey matches a key as well as a name, so a code still
    // resolves; anything unrecognised is sent as written for the api to reject.
    const resolveCountry = (): string | null => {
      const country = cell("addressCountryCode") || cell("addressCountryName");
      return country ? (findOptionKey(lookups.countryOptions, country) ?? country) : null;
    };

    // The template asks for the halves separately. An older file carries them together in one
    // "56.3722, -3.9855" cell, which is split only when the separate columns have nothing to say -
    // a sheet holding both should be answered by the ones the person was asked to fill in.
    const [pairedLatitude, pairedLongitude] = cell("locationCoordinates")
      .split(",")
      .map((part) => part.trim());
    const latitude = cell("latitude") || pairedLatitude;
    const longitude = cell("longitude") || pairedLongitude;

    rows.push({
      key: uuidv4(),
      // Counted from the heading row as row 0, so an error can point at the row to fix in the file.
      // By position rather than by project: a blank padding row still takes its number.
      rowNumber: bodyIndex + 1,
      displayName: cell("displayName"),
      projectTypeUuid: resolveDerived("projectTypeUuid", projectTypeName, lookups.projectTypeOptions),
      standardUuid: resolveDerived("standardUuid", standardName, lookups.standardOptions),
      vvbUuid: resolveDerived("verifierUuid", verifierName, lookups.verifierOptions),
      addressRegion: cell("addressRegion") || null,
      addressPostcode: cell("addressPostcode") || null,
      latitude: latitude || null,
      longitude: longitude || null,
      gridReference: cell("gridReference") || null,
      addressCountryCode: resolveCountry(),
      areaNetHa: toNumber(cell("areaNetHa")),
      projectTypeName: projectTypeName || undefined,
      standardName: standardName || undefined,
      verifierName: verifierName || undefined,
      landholdingName: cell("landholdingName") || null,
      landholdingReference: cell("landholdingReference") || null,
      landholdingReferenceId: cell("landholdingReferenceId") || null,
    });
  });

  assertLandholdingColumns(rows, indexes);

  return rows;
};
